import { getLastLiveCommentByUser } from '@/api/fbapi';
import ReplyComponent from '@/components/facebook/ReplyComponent';
import { AlertTriangle, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';

interface LiveCommentReplyFallbackProps {
  /** The Facebook PSID of the customer (used to look up their last live comment) */
  userId: string;
  /** Display name of the customer */
  userName: string;
  /** Called when the banner is dismissed */
  onDismiss: () => void;
}

/**
 * Shown inside the chat input area when `sendChatMessage` fails because
 * the customer only commented on an FB Live and has no Messenger thread.
 *
 * Strategy:
 *  1. Auto-fetch the last live comment ID for this user.
 *  2. If found → render ReplyComponent so staff can reply as a comment.
 *  3. If not found (API doesn't support the endpoint yet) → let staff
 *     paste a comment ID manually and still use ReplyComponent.
 */
export default function LiveCommentReplyFallback({
  userId,
  userName,
  onDismiss,
}: LiveCommentReplyFallbackProps) {
  const [loading, setLoading] = useState(true);
  const [commentId, setCommentId] = useState<string | null>(null);
  const [commentPreview, setCommentPreview] = useState<string | null>(null);
  const [manualId, setManualId] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [replied, setReplied] = useState(false);

  const fetchLastComment = async () => {
    setLoading(true);
    setFetchError(false);
    setCommentId(null);
    setCommentPreview(null);
    try {
      const resp = await getLastLiveCommentByUser(userId);
      if (resp.success && resp.data?.commentId) {
        setCommentId(resp.data.commentId);
        setCommentPreview(resp.data.message || null);
      } else {
        setFetchError(true);
      }
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setReplied(false);
    setManualId('');
    setShowManual(false);
    fetchLastComment();
  }, [userId]);

  const activeCommentId = commentId || (manualId.trim() || null);

  if (replied) {
    return (
      <div className="mx-2 mb-2 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 shadow-sm">
        <span className="text-base">✅</span>
        <span className="font-medium">Reply sent as a Facebook comment.</span>
        <button
          type="button"
          onClick={onDismiss}
          className="ml-auto text-xs text-emerald-600 underline hover:text-emerald-800"
        >
          Dismiss
        </button>
      </div>
    );
  }

  return (
    <div className="mx-2 mb-2 rounded-xl border border-amber-200 bg-amber-50 shadow-sm overflow-hidden">
      {/* ── Header ── */}
      <div className="flex items-start gap-3 px-4 py-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-800">
            Can't send via Messenger
          </p>
          <p className="text-xs text-amber-700 mt-0.5">
            The messaging window with <strong>{userName}</strong> is closed or they only commented on a Facebook Live.
            You can reply to their comment directly on Facebook instead.
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-amber-400 hover:text-amber-700 transition text-lg leading-none"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>

      {/* ── Body ── */}
      <div className="px-4 pb-3 space-y-3">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center gap-2 text-xs text-amber-700">
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            Looking up their last comment…
          </div>
        )}

        {/* Fetch succeeded → show ReplyComponent */}
        {!loading && commentId && (
          <div className="space-y-2">
            {commentPreview && (
              <div className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs text-gray-700">
                <span className="font-semibold text-amber-700 block mb-1">Last comment</span>
                <p className="italic truncate">"{commentPreview}"</p>
              </div>
            )}
            <ReplyComponent
              authorName={userName}
              commentId={commentId}
              onClose={() => setReplied(true)}
            />
          </div>
        )}

        {/* Fetch failed → manual input */}
        {!loading && fetchError && (
          <div className="space-y-2">
            <p className="text-xs text-amber-700">
              Could not auto-fetch the last comment. Paste the comment ID from Facebook to reply manually:
            </p>
            <button
              type="button"
              onClick={() => setShowManual((v) => !v)}
              className="flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900"
            >
              {showManual ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {showManual ? 'Hide manual input' : 'Enter comment ID manually'}
            </button>

            {showManual && (
              <div className="space-y-2">
                <input
                  type="text"
                  value={manualId}
                  onChange={(e) => setManualId(e.target.value)}
                  placeholder="Paste Facebook comment ID…"
                  className="w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                {activeCommentId && (
                  <ReplyComponent
                    authorName={userName}
                    commentId={activeCommentId}
                    onClose={() => setReplied(true)}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* Retry button after fetch error */}
        {!loading && fetchError && (
          <button
            type="button"
            onClick={fetchLastComment}
            className="flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-900 font-medium"
          >
            <RefreshCw className="h-3 w-3" />
            Retry auto-fetch
          </button>
        )}
      </div>
    </div>
  );
}
